import { useCallback, useMemo, useRef } from "react";
import useEvent from "react-use-event-hook";

import { jwtDecode } from "jwt-decode";

import apiOriginacao from "@services/axios/originacao";

/**
 * @param token Token JWT
 * @returns Timestamp em que o token expirará
 */
export const parseTokenExpireDate = (token?: string | null) => {
  if (!token) return 0;
  return (jwtDecode(token).exp || 0) * 1000;
};

/**
 * Tipo de retorno do hook `useRefreshToken`
 */
export type UseRefreshTokenResult = {
  refreshAccessToken: () => Promise<boolean>;
  ensureAccessToken: () => Promise<boolean>;
};

/**
 * Retorna duas funções que lidam com a atualização do refresh token:
 * `refreshAccessToken`: uma função para atualizar o `accessToken` manualmente que retorna true se token for atualizado com sucesso.
 * `ensureAccessToken`: uma função para atualizar o `accessToken` manualmente apenas quando o token estiver prestes a expirar, retorna true se token atual for válido ou tiver sido atualizado com sucesso.
 */
export const useRefreshToken = ({
  refreshToken,
  accessToken,
  onUpdateAccessToken,
  onExpireSession,
}: {
  accessToken?: string | null;
  refreshToken?: string | null;
  onUpdateAccessToken: (accessToken: string) => void;
  onExpireSession: () => void;
}): UseRefreshTokenResult => {
  const refreshRequestRef = useRef<Promise<boolean>>();

  const onUpdateAccessTokenRef = useRef(onUpdateAccessToken);
  onUpdateAccessTokenRef.current = onUpdateAccessToken;

  const onExpireSessionRef = useRef(onExpireSession);
  onExpireSessionRef.current = onExpireSession;

  const accessTokenExpiration = useMemo(
    () => parseTokenExpireDate(accessToken),
    [accessToken],
  );

  const refreshTokenExpiration = useMemo(
    () => parseTokenExpireDate(refreshToken),
    [refreshToken],
  );

  const getRemainingAccessTokenTime = useCallback(
    () => accessTokenExpiration - Date.now(),
    [accessTokenExpiration],
  );

  const getRemainingRefreshTokenTime = useCallback(
    () => refreshTokenExpiration - Date.now(),
    [refreshTokenExpiration],
  );

  /**
   * Realiza uma chamada para atualizar o `accessToken` do usuário.
   *
   * Expira a sessão se o `refreshToken` não for válido, estiver expirado, ou se houver um erro na requisição.
   */
  const refreshAccessToken = useEvent(async () => {
    const fetchToken = async () => {
      const hasExpiredRefreshToken = getRemainingRefreshTokenTime() <= 0;

      if (!refreshToken || hasExpiredRefreshToken) {
        onExpireSessionRef.current();
        return false;
      }

      try {
        const { data } = await apiOriginacao.post<{
          access: string;
          refresh: string;
        }>("/auth/token/refresh/", { refresh: refreshToken });

        onUpdateAccessTokenRef.current(data.access);

        return true;
      } catch (e) {
        console.error(
          "Ocorreu um erro ao atualizar o token de acesso do usuário.",
          e,
        );
        onExpireSessionRef.current();
        return false;
      }
    };

    if (!refreshRequestRef.current) {
      refreshRequestRef.current = fetchToken().finally(() => {
        refreshRequestRef.current = undefined;
      });
    }

    return refreshRequestRef.current;
  });

  /**
   * Atualiza o `accessToken` apenas quando o mesmo estiver prestes a expirar.
   */
  const ensureAccessToken = useEvent(async () => {
    if (!accessToken) return false;
    const remainingAccessTokenTime = getRemainingAccessTokenTime();
    if (remainingAccessTokenTime >= 5000) return true;
    return refreshAccessToken();
  });

  return { refreshAccessToken, ensureAccessToken };
};
